import { useEffect, useRef, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { createProspectPadLinkToken } from '../../services/tenantApi';
import {
  messageForPlaidLinkExit,
  type PlaidLinkExitError,
} from '../../utils/plaidLinkExitMessage';
import { CLOSING_BUTTON, CLOSING_BUTTON_DISABLED } from './closing-styles';

interface ClosingPadPlaidButtonProps {
  getToken: () => Promise<string>;
  onExchange: (publicToken: string) => Promise<void>;
}

let pendingLinkToken: Promise<string> | null = null;

/** One in-flight token request, so a remount does not ask Plaid twice. */
function requestLinkToken(getToken: () => Promise<string>): Promise<string> {
  if (pendingLinkToken) return pendingLinkToken;
  pendingLinkToken = createProspectPadLinkToken(getToken).finally(() => {
    pendingLinkToken = null;
  });
  return pendingLinkToken;
}

interface PlaidLinkSessionProps extends ClosingPadPlaidButtonProps {
  onSessionEnd: (message: string | null) => void;
}

function PlaidLinkSession({ getToken, onExchange, onSessionEnd }: PlaidLinkSessionProps) {
  const getTokenRef = useRef(getToken);
  const onExchangeRef = useRef(onExchange);
  const onSessionEndRef = useRef(onSessionEnd);
  getTokenRef.current = getToken;
  onExchangeRef.current = onExchange;
  onSessionEndRef.current = onSessionEnd;

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const opened = useRef(false);

  useEffect(() => {
    let cancelled = false;
    requestLinkToken(() => getTokenRef.current())
      .then((token) => {
        if (!cancelled) setLinkToken(token);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          onSessionEndRef.current(
            err instanceof Error ? err.message : 'Bank connection is unavailable right now.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (publicToken) => {
      setConnecting(true);
      onSessionEndRef.current(null);
      void onExchangeRef.current(publicToken)
        .catch((err: unknown) => {
          onSessionEndRef.current(
            err instanceof Error ? err.message : 'The bank account did not connect. Try again.',
          );
        })
        .finally(() => setConnecting(false));
    },
    onExit: (exitError) => {
      const message = messageForPlaidLinkExit(exitError as PlaidLinkExitError | null);
      onSessionEndRef.current(message);
    },
  });

  useEffect(() => {
    if (!ready || opened.current) return;
    opened.current = true;
    open();
  }, [ready, open]);

  return (
    <button type="button" className={CLOSING_BUTTON_DISABLED} disabled>
      {connecting ? 'Sending agreement…' : 'Select your bank account to autofill the PAD form'}
    </button>
  );
}

export default function ClosingPadPlaidButton(props: ClosingPadPlaidButtonProps) {
  const [started, setStarted] = useState(false);
  const [error, setError] = useState('');

  const handleSessionEnd = (message: string | null) => {
    if (message) setError(message);
    setStarted(false);
  };

  return (
    <div className="flex flex-col items-start gap-2">
      {error ? (
        <p className="text-sm text-red-700 dark:text-red-400" role="alert">
          {error}
        </p>
      ) : null}
      {!started ? (
        <button
          type="button"
          className={CLOSING_BUTTON}
          onClick={() => {
            setError('');
            setStarted(true);
          }}
        >
          Select your bank account to autofill the PAD form
        </button>
      ) : (
        <PlaidLinkSession {...props} onSessionEnd={handleSessionEnd} />
      )}
    </div>
  );
}
